import React, { useCallback, useRef } from 'react'
import { Button, Grid, Icon } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import { Field, FieldArray, reduxForm } from 'redux-form' // ES6
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import styles from 'modules/Forms/Tickets/NewTicketMessage/new-ticket-message.module.scss'
import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import renderFileUploader from 'components/FormControls/fileUploader'
import { CREATE, SEND_MESSAGE } from 'modules/Tickets/types'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import { useSelector } from 'react-redux'
import i18next from 'i18next'

const validationSchema = (isNew) =>
  isNew
    ? Yup.object().shape({
        title: Yup.string()
          .max(
            255,
            i18next.t('common::validation_messages.incorrect_text_length_max', {
              limit: 255,
            }),
          )
          .required('common::validation_messages.required'),
        text: Yup.string()
          .trim()
          .required('common::validation_messages.required'),
        attachments: Yup.array().nullable(),
      })
    : Yup.object().shape({
        text: Yup.string()
          .trim()
          .required('common::validation_messages.required'),
        attachments: Yup.array().nullable(),
      })

let NewTicketMessage = ({
  id,
  handleSubmit,
  form,
  onSuccess,
  reset,
  newTicket,
}) => {
  const { t } = useTranslation()
  const { values } = useSelector((state) => state.form[form])
  const FormRef = useRef()
  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) =>
        dispatch({
          meta: { resolve, reject },
          type: newTicket ? CREATE : SEND_MESSAGE,
          payload: {
            id,
            data: values,
            formName: form,
            meta: { resolve, reject },
            newTicket,
          },
        }),
      ).then((response) => {
        onSuccess(response)
        reset()
      }),
    [onSuccess, newTicket],
  )

  return (
    <form
      onSubmit={handleSubmit(submit)}
      noValidate
      style={{ width: '100%' }}
      ref={FormRef}
    >
      <Grid
        flexDirection={'column'}
        className={clsx(styles['new-message-form__container'], {
          [styles['new-message-form__container_new-ticket']]: newTicket,
        })}
      >
        <FieldArray
          buttonId={'upload-button-id'}
          name="attachments"
          component={renderFileUploader}
          label={t('common::uploader.add_file')}
          rowClassName={styles['new-message-form__attachments-row']}
        />

        {newTicket && (
          <Field
            className={'margin-bottom-16'}
            fullWidth
            autoComplete="off"
            type="text"
            component={renderInputField}
            label={t('tickets::new_message.title.label')}
            onKeyDown={(e) => {
              e.keyCode === 13 &&
                (e.ctrlKey === true || e.metaKey === true) &&
                FormRef.current.requestSubmit()
            }}
            t={t}
            name={'title'}
            normalize={(value = '') => value.substr(0, 255)}
            helperText={
              values?.title?.length > 0 &&
              t('{{length}}/255', {
                length: values?.title?.length || 0,
              })
            }
          />
        )}

        <div className={clsx(styles['new-message-form__message-container'])}>
          <Field
            fullWidth
            autoComplete="off"
            component={renderInputField}
            label={t('tickets::new_message.input.label')}
            t={t}
            onKeyDown={(e) => {
              e.keyCode === 13 &&
                (e.ctrlKey === true || e.metaKey === true) &&
                FormRef.current.requestSubmit()
            }}
            InputProps={{
              className: styles['new-message-form__textarea'],
              endAdornment: (
                <div>
                  <Button
                    className={styles['new-message-form__add-file-button']}
                    onClick={() =>
                      document.getElementById('upload-button-id').click()
                    }
                    noLabel
                    color={'Interface-Seventh'}
                    size={'m'}
                    variant={'ghost'}
                    startIcon={<Icon name={'attachment'} />}
                  />

                  <Button
                    disabled={values?.text?.trim() === ''}
                    className={styles['new-message-form__submit-button']}
                    type={'submit'}
                    noLabel
                    color={'Interface-Seventh'}
                    size={'m'}
                    variant={'ghost'}
                    startIcon={<Icon name={'send-message'} />}
                  />
                </div>
              ),
            }}
            multiline={true}
            name={'text'}
          />
        </div>
      </Grid>
    </form>
  )
}

NewTicketMessage = reduxForm({
  form: 'NewTicketMessageForm',
  shouldAsyncValidate: shouldAsyncValidate,
  initialValues: {},
})(NewTicketMessage)

NewTicketMessage.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  pristine: PropTypes.bool,
  newTicket: PropTypes.bool,
  id: PropTypes.string,
  autofill: PropTypes.func,
  reset: PropTypes.func,
  onSuccess: PropTypes.func,
  form: PropTypes.string,
  UploadButton: PropTypes.elementType,
}

export default NewTicketMessage

export { validationSchema as validationSchema }
